import type { MouseEvent } from "react";
import { Avatar, Box, ButtonBase, Typography, useTheme } from "@mui/material";
import TimelineDot from "@mui/lab/TimelineDot";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import type { DrivingSchool } from "../providers/drivingSchoolsProvider";
import type { Student } from "../providers/studentsProvider";
import { getStudentStatusDisplay, studentStatusColors } from "../providers/studentsProvider";
import { dotStyle } from "../misc/backofficeTheme";
import { StudentLabels } from "../misc/StudentLabels";
import { Column } from "../misc/Column";
import { Row } from "../misc/Row";
import { formatCents } from "../misc/Money";
import { useThemeColor } from "../hooks/useThemeColor";

const kpiNumberFormatter = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

export function StudentMobileListItem(props: {
  drivingSchool: DrivingSchool;
  showSimulatorDrivingLessons: boolean;
  student: Student;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
}) {
  const { drivingSchool, showSimulatorDrivingLessons, student, onClick } = props;
  const theme = useTheme();
  const errorColor = useThemeColor("error").main;
  const assignedBranchId = student.branchId ?? drivingSchool.defaultBranch?.uid;
  const assignedBranchName = assignedBranchId
    ? (drivingSchool.branchesById[assignedBranchId]?.name ?? "Keine Filiale")
    : undefined;
  const { label: displayStatus, color: statusColor } = getStudentStatusDisplay(student.verboseStatus);
  const isOutlinedStatus = ["Inaktiv", "Abgebrochen"].includes(student.verboseStatus);
  const kpis = [
    `${formatKpiNumber(student.kpis.finishedTheoryLessonsCount)} Theorie`,
    ...(showSimulatorDrivingLessons
      ? [`${formatKpiNumber(student.kpis.finishedSimulatorDrivingLessonsCount)} Simulator`]
      : []),
    `${formatKpiNumber(student.kpis.finishedNormalDrivingLessonsCount)} Fahrstunden`,
    `${formatKpiNumber(student.kpis.finishedSpecialDrivingLessonsCount)} Sonderfahrten`,
  ];

  return (
    <ButtonBase
      component="div"
      role="button"
      tabIndex={0}
      onClick={onClick}
      sx={{
        alignItems: "stretch",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        display: "block",
        px: 0,
        py: 1,
        textAlign: "left",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Avatar src={student.avatarUrl} sx={{ height: 40, width: 40 }} />
        <Column sx={{ flex: 1, gap: 0.5, minWidth: 0 }}>
          <Column sx={{ gap: 0.25, minWidth: 0 }}>
            <StudentLabels student={student} size="small" />
            <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {student.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[assignedBranchName, student.bookedTrainingsSummary].filter(Boolean).join(" · ")}
            </Typography>
          </Column>
          <Typography variant="caption" color="text.secondary">
            {kpis.join(" · ")}
          </Typography>
          <Row sx={{ alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
              {student.verboseStatus === "Fertig" ? (
                <CheckCircleIcon sx={{ color: studentStatusColors.Aktiv, flexShrink: 0, fontSize: 13 }} />
              ) : (
                <TimelineDot
                  sx={{
                    ...dotStyle,
                    width: 10,
                    height: 10,
                    flexShrink: 0,
                    padding: 0,
                    ...(isOutlinedStatus
                      ? {
                          borderStyle: "solid",
                          borderWidth: 1.5,
                          borderColor: "text.disabled",
                          backgroundColor: "background.paper",
                        }
                      : { border: "none", backgroundColor: statusColor }),
                  }}
                />
              )}
              <Typography variant="caption" noWrap>
                {displayStatus}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: student.balance < 0 ? errorColor : undefined, flexShrink: 0 }}>
              {formatCents(student.balance)}
            </Typography>
          </Row>
        </Column>
      </Box>
    </ButtonBase>
  );
}

function formatKpiNumber(value: number): string {
  return kpiNumberFormatter.format(value);
}
